import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ROLE_STATEMENT } from '@/legal/legalContent';
import {
  Shield, ArrowRight, Camera, Truck, CreditCard,
  Globe, Building2, Zap, Store, MapPin, Phone, ExternalLink,
  Fingerprint, ShieldCheck, Package, Sparkles, ClipboardCheck, Plug,
  RefreshCcw, PackageX, Home, Landmark, PackageOpen, Undo2
} from 'lucide-react';

const PLATFORMS = [
  { id: 'sellesend',   name: 'SelleSend',           desc: 'Shipping & Protection',    url: '/',           icon: Shield,     color: 'from-emerald-500 to-blue-500',  internal: true, active: true },
  { id: 'selletrans',  name: 'SelleSend (Parcel Risk)', desc: 'Freight Coverage',         url: '/selletrans', icon: ShieldCheck,color: 'from-purple-500 to-indigo-500', internal: true },
  { id: 'sellecheck',  name: 'SelleCheck',          desc: 'Verification & Compliance', url: 'https://sellecheck.com', icon: Shield, color: 'from-blue-600 to-indigo-600' },
  { id: 'sellepack',   name: 'SelleGard Freight',   desc: 'Discounted Shipping',      url: '/dashboard/sellegard', icon: Truck, color: 'from-amber-500 to-orange-600', internal: true },
];

const PIC_N_CLICK_STEPS = [
  { n: 1, icon: Camera,      title: 'Snap a photo',       body: 'Point your phone at the item. Any angle, any lighting. We do the rest.' },
  { n: 2, icon: Fingerprint, title: 'We fingerprint it',  body: 'A forensic hash is generated and anchored in a Merkle-tree ledger with published roots — the record cannot be changed later.' },
  { n: 3, icon: Package,     title: 'Dimensions + value', body: 'We estimate weight and dimensions from the image, and offer an authenticity score if we can verify it.' },
  { n: 4, icon: Truck,       title: 'Cover & ship',      body: 'Pick a coverage tier, choose your company and get connected. Verification number stamped on the report.' },
];

const COVERAGE_TIERS = [
  { code: 'A',    tag: 'Full shipping',    price: 'Custom',  bullet: 'Loss + damage up to declared value',                          tone: 'emerald' },
  { code: 'B',    tag: 'Co-share',         price: 'Custom',  bullet: 'Shared deductible · payout scaled to shipment',         tone: 'blue' },
  { code: 'C',    tag: 'Chargeback+',      price: 'Custom',  bullet: 'Shipping + chargeback protection',                            tone: 'indigo' },
  { code: 'D',    tag: 'Complete',         price: 'Custom',  bullet: '+ porch piracy, shortage, returns, customs',                  tone: 'purple' },
  { code: 'E',    tag: 'Flat price',       price: 'Custom',  bullet: 'High-value flat-premium bands',                               tone: 'amber' },
];

// Mike, 15-oct ("SelleSend Website Copy and Developer Flow"): tres pasos y
// "Choose your company and get connected" en lugar de comprar una etiqueta.
// Sin nombres de transportistas en las paginas publicas.
const HOW_IT_WORKS = [
  { icon: ClipboardCheck, title: 'Apply',                body: 'Tell us about your business, what you ship, and the services you need.' },
  { icon: Plug,           title: 'Connect your account', body: 'Connect your eligible shipping account and sales channels, or provide account details through our secure setup process.' },
  { icon: Truck,          title: 'Ship',                 body: 'Create your shipment, select available services and protection options, and track delivery.' },
];

const tone = (t) => ({
  emerald: 'from-emerald-500 to-green-500',
  blue:    'from-blue-500 to-cyan-500',
  indigo:  'from-indigo-500 to-blue-500',
  purple:  'from-purple-500 to-pink-500',
  amber:   'from-amber-500 to-orange-500',
}[t]);

const LandingPage = () => (
  <div className="min-h-screen bg-background">
    {/* ---------- NAV ---------- */}
    <nav className="glass sticky top-0 z-50 border-b border-slate-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg shadow-emerald-500/30">
            <img src="/assets/logo/sellesend-logo.png" alt="SelleSend" className="w-full h-full object-contain" />
          </div>
          <div className="flex flex-col">
            <span className="text-2xl font-bold tracking-tight bg-gradient-to-r from-emerald-500 to-blue-500 bg-clip-text text-transparent">SelleSend</span>
            <span className="text-[11px] text-emerald-600 -mt-0.5">Global Payments · Shipping · Protection</span>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/selletrans"><Button variant="ghost" className="hidden sm:inline-flex" data-testid="nav-selletrans">Parcel Risk</Button></Link>
          <Link to="/login"><Button variant="ghost" className="text-emerald-600" data-testid="login-nav-btn">Login</Button></Link>
          <Link to="/register">
            <Button className="btn-press bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600" data-testid="get-started-nav-btn">
              Get Started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </nav>

    {/* ---------- PLATFORM BAR ---------- */}
    <section className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-3" data-testid="platform-bar">
      <div className="max-w-7xl mx-auto px-6 flex items-center gap-2 overflow-x-auto">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mr-2 flex-shrink-0">PLATFORMS</span>
        {PLATFORMS.map(p => {
          const Icon = p.icon;
          const cls = "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm flex-shrink-0";
          if (p.internal) {
            return (
              <Link key={p.id} to={p.url}
                className={cls + (p.active
                  ? ' bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                  : ' border border-slate-200 dark:border-slate-700 hover:border-slate-400')}
                data-testid={`platform-link-${p.id}`}>
                <div className={`w-6 h-6 rounded bg-gradient-to-br ${p.color} flex items-center justify-center`}>
                  <Icon className="w-3.5 h-3.5 text-white" />
                </div>
                <span className={p.active ? 'font-semibold text-emerald-700 dark:text-emerald-300' : 'font-medium'}>{p.name}</span>
              </Link>
            );
          }
          return (
            <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer"
              className={cls + " border border-slate-200 dark:border-slate-700 hover:border-slate-400"}
              data-testid={`platform-link-${p.id}`}>
              <div className={`w-6 h-6 rounded bg-gradient-to-br ${p.color} flex items-center justify-center`}>
                <Icon className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="font-medium">{p.name}</span>
              <ExternalLink className="w-3 h-3 text-slate-300" />
            </a>
          );
        })}
      </div>
    </section>

    {/* ---------- HERO (luxury photo preserved) ---------- */}
    <section className="hero-gradient py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p className="text-xs font-mono uppercase tracking-widest text-emerald-500 mb-4">
            SHIP · INSURE · GET PAID · REPEAT
          </p>
          <h1 className="text-5xl md:text-7xl font-semibold tracking-tight mb-6">
            <span className="bg-gradient-to-r from-emerald-600 via-green-600 to-blue-600 bg-clip-text text-transparent">Global Commerce.</span><br />
            <span className="bg-gradient-to-r from-blue-500 to-emerald-500 bg-clip-text text-transparent">One Platform.</span>
          </h1>
          <p className="text-lg md:text-2xl text-slate-600 dark:text-slate-300 leading-relaxed mb-8 max-w-2xl">
            Take a picture. We verify it, we cover it, we ship it, you get paid.
            <span className="text-emerald-600 font-medium"> Lost, damaged or charged back, you get paid back instead of chasing it.</span>
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register">
              <Button size="lg" className="btn-press h-14 px-8 text-lg shadow-lg shadow-emerald-500/20 bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600" data-testid="hero-get-started-btn">
                Start shipping <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/selletrans">
              <Button variant="outline" size="lg" className="h-14 px-8 text-lg border-emerald-500 text-emerald-600 hover:bg-emerald-50" data-testid="hero-selletrans-btn">
                See Parcel Risk coverage
              </Button>
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/cc797109-9c87-4f32-bfe1-6841f3e70192/images/dcba278adf3d2db75709d8e6d9ba05d043834f38db760dfda9abfac81f4ce39c.png"
              alt="Luxury goods — designer handbags, gold watches, jewelry, premium accessories"
              className="rounded-2xl shadow-2xl"
            />
            <div className="absolute -bottom-6 -left-6 bg-white dark:bg-slate-900 rounded-xl p-4 shadow-xl border border-emerald-200 dark:border-emerald-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                  <Globe className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-emerald-500">Global Reach</p>
                  <p className="font-semibold tabular-nums text-emerald-700">100 Countries</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ---------- HERO PROMISE STRIP (right under the luxury photo) ---------- */}
    <section className="py-14 lg:py-20 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800" data-testid="hero-promise">
      <div className="max-w-7xl mx-auto px-6 space-y-14">

        {/* Top line — big brand promise */}
        <div className="text-center max-w-4xl mx-auto space-y-4">
          <p className="text-xs font-mono uppercase tracking-widest text-emerald-500">Protected Payments · 100+ Countries</p>
          {/* Texto de Mike, 15-oct: sin "powered by" ni nombres de procesadores. */}
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
            <span className="bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">Powerful payments. Built for your business.</span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 leading-relaxed">
            Accept payments and manage payouts through one streamlined platform—giving your business more control and your customers a simpler way to pay.
          </p>
        </div>

        {/* Protect your transactions — six pillar cards */}
        <div>
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2">Protect your transactions</h3>
            <p className="text-slate-500 dark:text-slate-400">Six kinds of cover. Choose the tier you need, shipment by shipment.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="protect-pillars">
            {[
              { icon: RefreshCcw,  title: 'Chargeback Protection',      color: 'from-emerald-500 to-green-500',   desc: 'A hashed, timestamped coverage record to attach to the dispute.' },
              { icon: Truck,       title: 'Shipping Protection',        color: 'from-blue-500 to-cyan-500',       desc: 'Full replacement value while the parcel is in transit.' },
              { icon: Home,        title: 'Porch Piracy',               color: 'from-purple-500 to-indigo-500',   desc: 'Delivered but stolen? We refund the buyer, you keep the sale.' },
              { icon: Landmark,    title: 'Customs Seizure Protection', color: 'from-amber-500 to-orange-500',    desc: 'Seized at the border — we cover it, you avoid the loss.' },
              { icon: PackageOpen, title: 'Shortage of Goods',          color: 'from-rose-500 to-pink-500',       desc: 'Box arrived missing items — buyer gets refunded from our pool.' },
              { icon: Undo2,       title: 'Return of Goods',            color: 'from-teal-500 to-cyan-600',       desc: 'Buyer returns for a valid reason — we absorb the return freight.' },
            ].map((p) => (
              <div key={p.title} className="group bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 hover:border-emerald-400 hover:shadow-lg transition-all" data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z]/g,'-')}`}>
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-semibold mb-1">{p.title}</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-500 dark:text-slate-400 text-center max-w-3xl mx-auto leading-relaxed" data-testid="protection-disclosure">
            Protection options vary by shipment and selected service. Insurance, warranties, and other protection programs are separate products with different terms. Review the applicable provider, limits, exclusions, and claim requirements before purchase.
          </p>
        </div>

        {/* Two-up: 300 shipping companies + Same-day nationwide */}
        <div className="grid md:grid-cols-2 gap-4" data-testid="hero-shipping-strip">
          <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-500 p-6 md:p-8 text-white relative overflow-hidden">
            <Truck className="absolute -right-4 -bottom-4 w-32 h-32 opacity-10" />
            <p className="text-xs font-mono uppercase tracking-widest opacity-80 mb-2">300 Shipping Companies</p>
            <h4 className="text-2xl md:text-3xl font-semibold mb-2 leading-tight">Ship protected with 300 shipping companies</h4>
            <p className="text-sm md:text-base opacity-90 leading-relaxed">Choose your company and get connected. One account, one bill, protection on every shipment you choose.</p>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 p-6 md:p-8 text-white relative overflow-hidden">
            <Zap className="absolute -right-4 -bottom-4 w-32 h-32 opacity-10" />
            <p className="text-xs font-mono uppercase tracking-widest opacity-80 mb-2">Same-Day Nationwide</p>
            <h4 className="text-2xl md:text-3xl font-semibold mb-2 leading-tight">Same-day courier delivery</h4>
            <p className="text-sm md:text-base opacity-90 leading-relaxed">Courier partners are being connected now. Same-day is not live yet, and this page will say so until it is.</p>
          </div>
        </div>

      </div>
    </section>

    {/* ---------- PIC-N-CLICK SHOWCASE ---------- */}
    <section className="py-20 lg:py-24 bg-gradient-to-br from-slate-50 to-emerald-50 dark:from-slate-900 dark:to-emerald-950" data-testid="picnclick-showcase">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs font-mono uppercase tracking-widest text-emerald-500 mb-3">PIC-N-CLICK</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">One photo. One tap. Covered.</h2>
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            Our forensic fingerprint hashes your item into a Merkle-tree ledger with published roots, so the record cannot be altered after the fact. Weight, dimensions, and declared value are computed on the spot.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PIC_N_CLICK_STEPS.map(s => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 hover:shadow-xl transition-all" data-testid={`pnc-step-${s.n}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-slate-200 dark:text-slate-800 tabular-nums">{String(s.n).padStart(2, '0')}</div>
                </div>
                <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-base leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <Link to="/register">
            <Button size="lg" className="h-14 px-8 text-lg bg-gradient-to-r from-emerald-500 to-blue-500" data-testid="pnc-cta">
              Try Pic-n-Click free <Camera className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>

    {/* ---------- HOW IT WORKS ---------- */}
    <section className="py-20 lg:py-24 bg-white dark:bg-slate-950" data-testid="how-it-works">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">THE PROCESS</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Shipping as easy as 1-2-3</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">Connect your business to shipping, payment tools, and protection options through one streamlined platform.</p>
        </div>
        <ol className="space-y-4">
          {HOW_IT_WORKS.map((step, i) => {
            const Icon = step.icon;
            return (
              <li key={i} className="flex items-start gap-6 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-emerald-300 dark:hover:border-emerald-800 transition-all" data-testid={`how-step-${i + 1}`}>
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-3 mb-1">
                    <span className="text-2xl font-bold text-slate-300 dark:text-slate-700 tabular-nums">STEP {i + 1}</span>
                  </div>
                  <h3 className="text-2xl font-semibold mb-1">{step.title}</h3>
                  <p className="text-lg text-slate-600 dark:text-slate-300">{step.body}</p>
                </div>
              </li>
            );
          })}
        </ol>
        <div className="text-center mt-10">
          <Link to="/register">
            <Button size="lg" className="btn-press h-14 px-10 text-lg bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600" data-testid="how-apply-btn">
              Apply Now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
        <div className="text-center mt-14 max-w-2xl mx-auto" data-testid="how-supporting">
          <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2">Your business. A simpler web world.</h3>
          <p className="text-lg text-slate-600 dark:text-slate-400">Access service networks, simplify daily operations, and explore new ways to reach customers.</p>
        </div>
      </div>
    </section>

    {/* ---------- PARCEL RISK COVERAGE ---------- */}
    <section className="py-20 lg:py-24 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-slate-950 dark:to-purple-950/30" data-testid="selletrans-tiers">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs font-mono uppercase tracking-widest text-purple-500 mb-3">PARCEL RISK COVERAGE</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Five coverage tiers. Pick one.</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto">
            Every SelleSend shipment can be upgraded with Parcel Risk coverage. Every quote issues a verification number and a signed audit report.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          {COVERAGE_TIERS.map(t => (
            <div key={t.code} className="bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800 hover:border-purple-400 transition-all" data-testid={`tier-card-${t.code}`}>
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tone(t.tone)} flex items-center justify-center mb-4 shadow-lg`}>
                <span className="text-2xl font-bold text-white">{t.code}</span>
              </div>
              <p className="text-2xl font-bold mb-1 tabular-nums">{t.price}</p>
              <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{t.tag}</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">{t.bullet}</p>
            </div>
          ))}
        </div>
        <div className="text-center">
          <Link to="/selletrans">
            <Button size="lg" variant="outline" className="h-14 px-8 text-lg border-purple-500 text-purple-600 hover:bg-purple-50" data-testid="tiers-more-btn">
              Compare all coverage → Parcel Risk <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>

    {/* ---------- STATS ---------- */}
    <section className="py-16 bg-slate-50 dark:bg-slate-900" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          {/* Sin "Partnerships" ni contador de volumen de pagos (documento de Mike, 15-oct). */}
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">SERVICE PROVIDERS</p>
          <h2 className="text-3xl md:text-4xl font-bold">The Scale of Our Network</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { icon: Truck,      val: '300+',  lbl: 'shipping companies', c: 'text-amber-500'   },
            { icon: CreditCard, val: '200',   lbl: 'currencies',         c: 'text-blue-500'    },
            { icon: Globe,      val: '100',   lbl: 'countries',          c: 'text-purple-500'  },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl p-6 text-center shadow-lg border border-slate-200 dark:border-slate-700">
                <Icon className={`w-10 h-10 mx-auto mb-3 ${s.c}`} />
                <p className="text-4xl font-bold tabular-nums">{s.val}</p>
                <p className="text-sm text-slate-500 mt-1">{s.lbl}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* ---------- CTA ---------- */}
    <section className="py-20 bg-slate-900 text-white text-center relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/20 to-blue-600/20" />
      <div className="max-w-4xl mx-auto px-6 relative">
        <Sparkles className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
        <h2 className="text-4xl md:text-6xl font-bold mb-6">Ready to ship?</h2>
        <p className="text-xl md:text-2xl text-slate-300 mb-10">Three minutes to sign up. Choose your company and get connected. Cover on the shipments you choose.</p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/register">
            <Button size="lg" className="btn-press h-14 px-10 text-lg bg-gradient-to-r from-emerald-500 to-blue-500" data-testid="footer-cta">
              Start free <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <Link to="/selletrans">
            <Button size="lg" variant="outline" className="h-14 px-10 text-lg text-white border-white hover:bg-white/10">
              Learn about Parcel Risk
            </Button>
          </Link>
        </div>
      </div>
    </section>

    {/* ---------- BILLING TERMS ---------- */}
    <section className="py-16 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800" data-testid="billing-terms-section">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-8">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">BILLING TERMS</p>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Simple. Transparent. Non-Negotiable.</h2>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 md:p-10 border border-slate-200 dark:border-slate-700">
          <p className="text-lg text-slate-700 dark:text-slate-200 mb-4">Our programs give you optimal protection and convenience. Our billing policies are simple.</p>
          <p className="text-lg text-slate-700 dark:text-slate-200 mb-4">
            We expect to be paid on demand — SelleSend wallet, credit card, or certified funds.
          </p>
          <p className="text-lg font-semibold text-slate-900 dark:text-white">If your bills are not paid, your service and protection are not valid.</p>
        </div>
      </div>
    </section>

    {/* ---------- FOOTER ---------- */}
    <footer className="py-12 border-t border-slate-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-4"><Store className="w-6 h-6 text-primary" /><span className="text-xl font-semibold">SelleSend</span></div>
            <p className="text-sm text-slate-500">Global Payments · Shipping · Protection</p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <div className="space-y-2 text-sm text-slate-500">
              <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5" /><span>4395 Home Ave<br />San Diego, CA 92105</span></p>
              <p className="flex items-center gap-2"><Phone className="w-4 h-4" /><a href="tel:6199301074" className="hover:text-primary">(619) 930-1074</a></p>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">24/7 AI Support</h4>
            <div className="space-y-2 text-sm text-slate-500">
              <p className="flex items-center gap-2"><Phone className="w-4 h-4" /><span>After Hours (Verified Companies)</span></p>
              <p className="ml-6"><a href="tel:5104224451" className="hover:text-primary font-medium">(510) 422-4451</a></p>
              <p className="text-xs text-slate-400 ml-6">$10.00 per call · AI Agents Available</p>
            </div>
          </div>
        </div>
        {/* El rol de SelleSend con el texto que Mike mando el 16-oct, el mismo
            de las paginas legales (ROLE_STATEMENT en legalContent.js). */}
        <div
          className="mb-6 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-lg p-4 text-xs text-slate-600 dark:text-slate-300 leading-relaxed"
          data-testid="role-disclosure"
        >
          <p className="font-semibold text-slate-800 dark:text-slate-100 mb-1">Role of SelleSend</p>
          {ROLE_STATEMENT.map((parrafo, i) => (
            <p key={i} className="mb-2 last:mb-0">{parrafo}</p>
          ))}
        </div>
        <div className="pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Store className="w-4 h-4" />SelleSend © {new Date().getFullYear()} · 4395 Home Ave, San Diego CA 92105 · (619) 930-1074
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-slate-500">
            <a href="/regulatory"      className="hover:text-primary" data-testid="footer-regulatory">Regulatory</a>
            <a href="/shipping-terms"  className="hover:text-primary" data-testid="footer-shipping-terms">Shipping Terms</a>
            <a href="/privacy"         className="hover:text-primary" data-testid="footer-privacy">Privacy</a>
            <a href="/terms"           className="hover:text-primary" data-testid="footer-terms">Terms</a>
            <a href="/restricted-prohibited-products" className="hover:text-primary" data-testid="footer-restricted">Prohibited Items</a>
            <a href="/aml-compliance"  className="hover:text-primary" data-testid="footer-aml">AML</a>
            <a href="/legal"           className="hover:text-primary" data-testid="footer-legal">All Legal</a>
            <a href="mailto:support@sellesend.com" className="hover:text-primary" data-testid="footer-support">Support</a>
          </div>
        </div>
      </div>
    </footer>
  </div>
);

export default LandingPage;
